import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Trash2, User, UserPlus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useNetwork } from '@/contexts/NetworkContext'
import { apiErrorDescription } from '@/services/core/apiError'
import type { Network } from '@/services/networks/networksApi'
import { RolesApi } from '@/services/users/rolesApi'
import { UsersApi, type NetworkUser, type UserNetworkRole } from '@/services/users/usersApi'
import { FieldLabel } from './FieldLabel'
import { UserSelect } from './UserSelect'

interface Member {
  user: NetworkUser
  roles: string[]
}

/** The user's roles in the network, whichever way the network is referenced. */
function rolesInNetwork(user: NetworkUser, network: Network): UserNetworkRole | undefined {
  return user.userRoles?.rolesByNetwork?.find(
    (nr) => nr.network.tagId === network.tagId || nr.network.id === network.id
  )
}

/** What the roles are held on: one team, or every team in a team group. */
export type MembershipKind = 'team' | 'teamGroup'

/**
 * Re-saves a user's roles in the network with the team or team group role
 * added or taken away. The API replaces all of the user's roles in the
 * network, so the user is fetched fresh and every other role is sent back
 * unchanged.
 */
async function updateMembershipRole(
  userId: string,
  network: Network,
  kind: MembershipKind,
  id: string,
  roleId: string | null
): Promise<void> {
  const user = await UsersApi.getById(userId)
  const networkRoles = rolesInNetwork(user, network)
  const kept = (networkRoles?.roles ?? [])
    .filter((r) => r[kind]?.id !== id)
    .map((r) => ({
      role: r.role.id,
      ...(r.team ? { team: r.team.id } : {}),
      ...(r.teamGroup ? { teamGroup: r.teamGroup.id } : {}),
    }))
  const roles = roleId ? [...kept, { role: roleId, [kind]: id }] : kept
  await UsersApi.setNetworkRoles(userId, networkRoles?.network.id ?? network.id, roles)
}

interface TeamMembersCardProps {
  kind: MembershipKind
  /** The team's or team group's id. */
  id: string
}

/** The users holding a role on a team or team group, with adding and removing them. */
export function TeamMembersCard({ kind, id }: TeamMembersCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const [adding, setAdding] = useState(false)
  const [newUserId, setNewUserId] = useState('')
  const [newRoleId, setNewRoleId] = useState('')
  const [pendingRemove, setPendingRemove] = useState<NetworkUser | null>(null)

  const usersKey = ['membership-users', currentNetwork?.tagId]
  const { data: users = [], isLoading } = useQuery({
    queryKey: usersKey,
    queryFn: async () => (await UsersApi.getAll()).results,
    enabled: !!currentNetwork,
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: RolesApi.getAll,
  })
  // Only roles scoped to teams can be held on a team or team group.
  const teamRoles = roles.filter((r) => r.scope === 'TEAM')

  const members: Member[] = []
  const others: NetworkUser[] = []
  for (const user of users) {
    const held = currentNetwork
      ? (rolesInNetwork(user, currentNetwork)?.roles ?? []).filter((r) => r[kind]?.id === id)
      : []
    if (held.length) members.push({ user, roles: held.map((r) => r.role.name) })
    else others.push(user)
  }

  const mutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string | null }) =>
      updateMembershipRole(userId, currentNetwork!, kind, id, roleId),
    onSuccess: (_data, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: usersKey })
      toast.success(t('common.saved'), {
        description: roleId ? t('teamMembers.memberAdded') : t('teamMembers.memberRemoved'),
        duration: 3000,
      })
      setAdding(false)
      setPendingRemove(null)
    },
    onError: (error) => {
      toast.error(t('teamMembers.memberSaveFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const openAdd = () => {
    setNewUserId('')
    setNewRoleId(teamRoles[0]?.id ?? '')
    setAdding(true)
  }

  return (
    <Card tone="people">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={User} />
          {t('teamMembers.members', { count: members.length })}
        </CardTitle>
        <CardAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openAdd}
            disabled={isLoading || !others.length || !teamRoles.length}
          >
            <UserPlus className="size-3.5" />
            {t('teamMembers.addMember')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : members.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('teamMembers.noMembers', { context: kind })}
          </p>
        ) : (
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-(--ad-border) text-left text-xs text-(--ad-text-muted)">
                <th className="px-3 py-1.5 font-semibold">{t('teamMembers.user')}</th>
                <th className="px-3 py-1.5 font-semibold">{t('teamMembers.role')}</th>
                <th className="w-10 px-3 py-1.5">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map(({ user, roles: held }) => (
                <tr key={user.id} className="border-b border-(--ad-border) last:border-b-0">
                  <td className="px-3 py-1.5">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      {user.displayName || user.username}
                    </Button>
                    {user.displayName && (
                      <div className="text-xs text-muted-foreground">{user.username}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{held.join(', ')}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={t('teamMembers.removeMember')}
                      title={t('teamMembers.removeMember')}
                      onClick={() => setPendingRemove(user)}
                      disabled={mutation.isPending}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={adding} onOpenChange={(open) => !mutation.isPending && setAdding(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('teamMembers.addMember')}</DialogTitle>
            <DialogDescription>
              {t('teamMembers.addMemberDescription', { context: kind })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <FieldLabel>{t('teamMembers.user')}</FieldLabel>
              <UserSelect
                value={newUserId}
                onChange={setNewUserId}
                users={others.map((u) => ({
                  id: u.id,
                  displayName: u.displayName ? `${u.displayName} (${u.username})` : u.username,
                }))}
                placeholder={t('teamMembers.selectUser')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel tooltip={t('teamMembers.roleHelp', { context: kind })}>
                {t('teamMembers.role')}
              </FieldLabel>
              <Select label={t('teamMembers.role')} value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('teamMembers.selectRole')}>
                    {teamRoles.find((r) => r.id === newRoleId)?.name ?? null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {teamRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAdding(false)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              loading={mutation.isPending}
              disabled={!newUserId || !newRoleId}
              onClick={() => mutation.mutate({ userId: newUserId, roleId: newRoleId })}
            >
              <UserPlus className="size-4" />
              {t('teamMembers.addMember')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingRemove}
        onOpenChange={(open) => !open && !mutation.isPending && setPendingRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('teamMembers.removeMemberTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('teamMembers.removeMemberDescription', {
                context: kind,
                user: pendingRemove?.displayName || pendingRemove?.username,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(e) => {
                e.preventDefault()
                if (pendingRemove) mutation.mutate({ userId: pendingRemove.id, roleId: null })
              }}
            >
              {t('teamMembers.removeMember')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
